import { findRegimen } from "@/data/meal-plan";
import { DAYS_OF_WEEK, type DayPlan, type LegacySelection, type Selection, type WeekPlan } from "@/types/meal-plan-state";
import { getWeekDates, isValidWeekId } from "@/lib/week-utils";
import { normalize } from "@/lib/ingredient-mapping";
import { getMealChoices } from "@/lib/random-plan";

/**
 * A meal note, the days (YYYY-MM-DD, newest first) it was a dish of, and the
 * dish's foods: the carbs and proteins picked in its meals, A–Z.
 */
export interface NoteHistory {
  note: string;
  dates: string[];
  foods: string[];
}

/** A Comidas autocomplete option: on how many days the note was picked, the latest, and its foods. */
export interface NoteSuggestion {
  note: string;
  count: number;
  lastDate: string | null;
  foods: string[];
}

/**
 * The carbs and proteins picked in meal `index` of `day`: what the dish is
 * made of. Vegetables and fat are left out, as every dish gets the same
 * defaults ("Verduras frescas", "Palta"), and so are skipped groups. The
 * server reads stored plans as saved (unmigrated, possibly malformed), so
 * legacy `optionIndex` picks resolve too and anything else is ignored.
 */
function dishFoods(day: DayPlan, index: number): string[] {
  const meal = findRegimen(day.regimenId)?.meals[index];
  const picked = day.meals?.[index];
  if (!meal || !Array.isArray(picked)) return [];
  return getMealChoices(meal, day.mealMenus?.[index]).flatMap((group, i) => {
    const groupPicks = picked[i];
    const selections = groupPicks?.selections;
    if ((group.category !== "carb" && group.category !== "protein") || groupPicks?.skipped || !Array.isArray(selections)) return [];
    return selections.flatMap((raw: unknown) => {
      const s = (raw ?? {}) as Partial<Selection & LegacySelection>;
      if (!(typeof s.count === "number" && s.count > 0)) return [];
      const option =
        s.optionId !== undefined ? group.options.find((o) => o.id === s.optionId) : group.options[s.optionIndex ?? -1];
      return option ? [option.name] : [];
    });
  });
}

/**
 * The meal notes typed in week `weekId`, each with the date of its day (one
 * date when lunch and dinner share the note) and its meals' foods. No dates
 * when `weekId` isn't a week id (rows stored before ids were validated).
 */
export function weekNoteHistory(weekId: string, week: WeekPlan): NoteHistory[] {
  const dates = isValidWeekId(weekId) ? getWeekDates(weekId) : null;
  return mergeNoteHistories(
    DAYS_OF_WEEK.flatMap(({ id }, i) => {
      const day = week[id];
      const date = dates?.[i]?.date;
      return Object.entries(day?.mealNotes ?? {}).map(([index, note]) => ({
        note,
        dates: date ? [date] : [],
        foods: dishFoods(day, Number(index)),
      }));
    }),
  );
}

/**
 * Merges note histories: notes trimmed, blanks and non-strings dropped, one
 * entry per note ignoring case (the first spelling wins) with the dates
 * (newest first) and foods (A–Z) of every list united. `foods` may be
 * missing from a server that predates it.
 */
export function mergeNoteHistories(...lists: readonly NoteHistory[][]): NoteHistory[] {
  const byKey = new Map<string, { note: string; dates: Set<string>; foods: Set<string> }>();
  for (const list of lists) {
    for (const { note: raw, dates, foods } of list) {
      if (typeof raw !== "string") continue;
      const note = raw.trim();
      if (!note) continue;
      const key = note.toLowerCase();
      let entry = byKey.get(key);
      if (!entry) byKey.set(key, (entry = { note, dates: new Set(), foods: new Set() }));
      for (const date of dates) entry.dates.add(date);
      for (const food of foods ?? []) entry.foods.add(food);
    }
  }
  return [...byKey.values()].map(({ note, dates, foods }) => ({
    note,
    dates: [...dates].sort().reverse(),
    foods: [...foods].sort((a, b) => a.localeCompare(b, "es")),
  }));
}

/**
 * `history` without the days of week `weekId`: the caller has a newer copy of
 * that week (the one on screen). A note seen only in that week is dropped.
 * Foods aren't kept per day, so those of other days stay.
 */
export function withoutWeek(history: NoteHistory[], weekId: string): NoteHistory[] {
  if (!isValidWeekId(weekId)) return history;
  const week = new Set(getWeekDates(weekId).map((d) => d.date));
  return history.flatMap((h) => {
    const dates = h.dates.filter((d) => !week.has(d));
    return dates.length === 0 && h.dates.length > 0 ? [] : [{ ...h, dates }];
  });
}

/**
 * Comidas autocomplete options: the most recently picked first, then the most
 * picked; notes without dates last, in history order.
 */
export function noteSuggestions(history: NoteHistory[]): NoteSuggestion[] {
  return history
    .map((h) => ({ note: h.note, count: h.dates.length, lastDate: h.dates[0] ?? null, foods: h.foods }))
    .sort((a, b) => (b.lastDate ?? "").localeCompare(a.lastDate ?? "") || b.count - a.count);
}

/** An option shown for a query, and the food it matched by when its name didn't. */
export interface SuggestionMatch {
  suggestion: NoteSuggestion;
  food: string | null;
}

/**
 * The options for `query`, ignoring case and accents: those whose name
 * contains it, then those with a food with a word starting with it ("carne"
 * finds "Lomo saltado" by "Carne de res"). `exclude` (the input's saved note)
 * and the query itself are left out. An empty query keeps every option.
 */
export function matchSuggestions(suggestions: NoteSuggestion[], query: string, exclude: string): SuggestionMatch[] {
  const q = normalize(query.trim());
  const qWords = words(query);
  // Left out by spelling (case aside), so "Ají" stays offered for "aji".
  const excluded = exclude.trim().toLowerCase();
  const typed = query.trim().toLowerCase();
  const byName: SuggestionMatch[] = [];
  const byFood: SuggestionMatch[] = [];
  for (const suggestion of suggestions) {
    const spelling = suggestion.note.toLowerCase();
    if (spelling === excluded || spelling === typed) continue;
    const name = normalize(suggestion.note);
    if (name.includes(q)) {
      byName.push({ suggestion, food: null });
      continue;
    }
    const food = qWords ? suggestion.foods.find((f) => ` ${words(f)}`.includes(` ${qWords}`)) : undefined;
    if (food) byFood.push({ suggestion, food });
  }
  return [...byName, ...byFood];
}

/** `text` normalized to words split by single spaces: "Papa (sancochada)" → "papa sancochada". */
function words(text: string): string {
  return normalize(text).replace(/[^a-z0-9]+/g, " ").trim();
}

/** "hoy", "ayer", "hace 5 d", "hace 3 sem", "hace 4 meses", "hace 1 año"; ahead "mañana", "en 5 d"… */
export function relativeDay(date: string, today: string): string {
  const days = Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000);
  if (days === 0) return "hoy";
  if (days === -1) return "ayer";
  if (days === 1) return "mañana";
  const n = Math.abs(days);
  const years = Math.floor(n / 365);
  const span =
    n < 14 ? `${n} d` : n < 60 ? `${Math.floor(n / 7)} sem` : n < 365 ? `${Math.floor(n / 30)} meses` : `${years} ${years === 1 ? "año" : "años"}`;
  return days < 0 ? `hace ${span}` : `en ${span}`;
}
